import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import {
  getBankAccountObjectImportColumns,
  importBankAccountObjectAssignments,
} from "@accredo/fe-shared/services/bankAccountObjectAssignmentService.js";

// Adresse (alle vier) ODER Ref-ID muss zugeordnet sein - siehe mappingComplete.
// Ref-ID zuerst in der Liste, damit sie im Mapping-Schritt oben steht (hat
// beim Matching Vorrang, siehe BankAccountObjectImportService::matchObjects()).
// Kunde ohne object_ref_num_label hat laut Fachlogik keine Ref-IDs - das Feld
// entfällt dann komplett, Matching läuft nur noch über die Adresse.
const buildMappingFields = (objectRefNumLabel) => [
  ...(objectRefNumLabel
    ? [{ key: "customer_reference_number", label: objectRefNumLabel }]
    : []),
  { key: "plz", label: "PLZ" },
  { key: "ort", label: "Ort" },
  { key: "strasse", label: "Straße" },
  { key: "hausnr", label: "Hausnummer" },
];

const ADDRESS_FIELD_KEYS = ["plz", "ort", "strasse", "hausnr"];

const EMPTY_MAPPING = {
  plz: null,
  ort: null,
  strasse: null,
  hausnr: null,
  customer_reference_number: null,
};

// Spalten für Objekt-Treffer (to_add/unchanged/to_remove) - dieselbe Struktur wie
// die assignableObjects()-Response. accessorKey/header/size-Format, wie von
// DataTable.jsx (MaterialReactTable-Wrapper) erwartet.
const buildMatchColumns = (objectRefNumLabel) => [
  { accessorKey: "bezeichnung", header: "Bezeichnung", size: 200 },
  ...(objectRefNumLabel
    ? [
        {
          accessorKey: "customer_reference_number",
          header: objectRefNumLabel,
          size: 100,
        },
      ]
    : []),
  { accessorKey: "strasse", header: "Straße", size: 150 },
  { accessorKey: "hausnr", header: "Hausnr.", size: 90 },
  { accessorKey: "plz", header: "PLZ", size: 80 },
  { accessorKey: "ort", header: "Ort", size: 150 },
];

// Spalten für nicht gematchte Zeilen - zeigen die Rohwerte aus der Datei. Kein
// "Grund" mehr: Der Text ist ohnehin für jede Zeile identisch (siehe
// BankAccountObjectImportService::import()), stattdessen ein Hinweistext
// oberhalb der Tabelle (siehe renderPreviewStep()).
const buildUnmatchedColumns = (objectRefNumLabel) => [
  { accessorKey: "row", header: "Zeile", size: 70 },
  ...(objectRefNumLabel
    ? [
        {
          accessorKey: "customer_reference_number",
          header: objectRefNumLabel,
          size: 100,
        },
      ]
    : []),
  { accessorKey: "plz", header: "PLZ", size: 80 },
  { accessorKey: "ort", header: "Ort", size: 150 },
  { accessorKey: "strasse", header: "Straße", size: 150 },
  { accessorKey: "hausnr", header: "Hausnr.", size: 90 },
];

// Eine Tab pro Diff-Kategorie - Reihenfolge bestimmt auch die Priorität bei der
// automatischen Auswahl des zuerst angezeigten Tabs (siehe runPreview()).
const buildPreviewTabs = (objectRefNumLabel) => {
  const matchColumns = buildMatchColumns(objectRefNumLabel);

  return [
    { key: "to_add", label: "Neu", color: "success", columns: matchColumns },
    {
      key: "unmatched",
      label: "Fehler",
      color: "warning",
      columns: buildUnmatchedColumns(objectRefNumLabel),
    },
    { key: "to_remove", label: "Entfernen", color: "error", columns: matchColumns },
    { key: "unchanged", label: "Bereits zugeordnet", color: "default", columns: matchColumns },
  ];
};

const STEP = { UPLOAD: 0, MAPPING: 1, PREVIEW: 2, RESULT: 3 };

/**
 * Zusammenfassungstext für den Ergebnis-Schritt (Schritt 4), z. B. "3 Objekt(e)
 * hinzugefügt, 1 Objekt(e) entfernt. 2 waren bereits zugeordnet, 1 Zeile(n)
 * nicht gefunden." - spiegelt exakt wider, was der Apply-Aufruf laut $preview
 * getan hat (to_remove nur, wenn tatsächlich mit Löschen bestätigt wurde).
 */
function buildResultSummary(preview, deleteConfirmed) {
  const removedPart =
    deleteConfirmed && preview.to_remove.length > 0
      ? `, ${preview.to_remove.length} Objekt(e) entfernt`
      : "";
  const unmatchedPart =
    preview.unmatched.length > 0
      ? `, ${preview.unmatched.length} Zeile(n) nicht gefunden`
      : "";

  return `${preview.to_add.length} Objekt(e) hinzugefügt${removedPart}. ${preview.unchanged.length} waren bereits zugeordnet${unmatchedPart}.`;
}

function PreviewTable({ entries, columns }) {
  if (!entries || entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Keine Einträge.
      </Typography>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={entries}
      hasLoaded
      enableTopToolbar={false}
      enablePagination={false}
      enableColumnFilters={false}
      enableColumnHiding={false}
      enableSorting={false}
      // DataTable reicht unbekannte Props direkt an MaterialReactTable durch
      // (siehe DataTable.jsx, {...rest}) - so bekommt die Liste einen inneren
      // Scroll-Container mit fixierter Höhe statt den Dialog unbegrenzt wachsen
      // zu lassen, samt Sticky-Header beim Scrollen.
      enableStickyHeader
      muiTableContainerProps={{
        sx: {
          maxHeight: 280,
          overflowX: "auto",
          overflowY: "auto",
          borderLeft: 0,
          borderRight: 0,
        },
      }}
    />
  );
}

PreviewTable.propTypes = {
  entries: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessorKey: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
    })
  ).isRequired,
};

/**
 * Excel-Import-Dialog pro Bankkonto, vierstufiger Ablauf (MUI Stepper, "Zurück"
 * immer als outlined-Button, primäre Aktion jeweils im Dialog-Footer statt im
 * Content):
 * 1. Datei - Upload, Backend liest die Header-Zeile aus und schlägt ein
 *    Spalten-Mapping vor (PLZ/Ort/Straße/Hausnummer -> Spaltenindex).
 * 2. Spalten zuordnen - Vorschlag bestätigen/anpassen. Mindestens eine von
 *    zwei Matching-Strategien muss vollständig zugeordnet sein: Ref-ID
 *    (customer_reference_number, hat Vorrang) oder die komplette Adresse
 *    (PLZ/Ort/Straße/Hausnummer, Fallback). "Zurück" verwirft die Datei
 *    komplett (Schritt 1), "Weiter" lädt die Dry-Run-Vorschau.
 * 3. Vorschau - Tabs (Neu/Fehler/Entfernen/Bereits zugeordnet); falls das Tab
 *    "Entfernen" nicht leer ist, eine Checkbox, die die Löschkonsequenz
 *    explizit erläutert und Sync statt nur additiv erst nach Bestätigung
 *    erlaubt. "Zurück" verwirft die Vorschau (Datei/Mapping bleiben erhalten,
 *    Schritt 2). "Jetzt importieren" führt den Import sofort aus (kein
 *    weiterer Bestätigungs-Schritt vor der eigentlichen Ausführung) und
 *    springt danach zu Schritt 4.
 * 4. Importieren - reine Ergebnis-Bestätigung (was wurde hinzugefügt/entfernt),
 *    keine weitere Aktion mehr möglich außer "Schließen".
 */
export default function BankAccountObjectImportDialog({
  open,
  onClose,
  bankAccount,
  onSuccess,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();
  const fileInputRef = useRef(null);
  const objectRefNumLabel = bankAccount?.kunde?.object_ref_num_label;

  const mappingFields = useMemo(
    () => buildMappingFields(objectRefNumLabel),
    [objectRefNumLabel]
  );
  const previewTabs = useMemo(
    () => buildPreviewTabs(objectRefNumLabel),
    [objectRefNumLabel]
  );

  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState(null);
  const [columnMapping, setColumnMapping] = useState(EMPTY_MAPPING);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState(previewTabs[0].key);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [applied, setApplied] = useState(false);

  // Verwirft Vorschau + Bestätigung - für jede Änderung, die einen späteren
  // Schritt ungültig macht (Mapping geändert, zurück zum Mapping, neue
  // Vorschau wird geladen).
  const resetPreviewState = () => {
    setPreview(null);
    setDeleteConfirmed(false);
    setApplied(false);
  };

  // Verwirft zusätzlich Spalten-Mapping (und damit auch die Vorschau) - für
  // eine neu ausgewählte Datei oder den kompletten Neustart.
  const resetMappingState = () => {
    setColumns(null);
    setColumnMapping(EMPTY_MAPPING);
    resetPreviewState();
  };

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }
    setFile(null);
    resetMappingState();
    setOpenCD(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bankAccount?.id]);

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    resetMappingState();
    setIsLoadingColumns(true);
    try {
      const result = await getBankAccountObjectImportColumns(
        bankAccount.id,
        selectedFile
      );
      setColumns(result?.columns || []);
      setColumnMapping({ ...EMPTY_MAPPING, ...(result?.suggested_mapping || {}) });
    } catch (error) {
      enqueueSnackbar("Spalten der Datei konnten nicht gelesen werden.", {
        variant: "error",
      });
      console.error("Failed to read bank account object import columns", error);
    } finally {
      setIsLoadingColumns(false);
    }
  };

  const handleMappingChange = (field, value) => {
    setColumnMapping((prev) => ({ ...prev, [field]: value === "" ? null : Number(value) }));
    // Mapping geändert - vorherige Vorschau ist nicht mehr gültig.
    resetPreviewState();
  };

  // Mindestens eine der beiden Matching-Strategien muss vollständig zugeordnet
  // sein - Ref-ID allein oder die komplette Adresse (siehe
  // BankAccountObjectImportService::matchObjects()).
  const isReferenceMapped = columnMapping.customer_reference_number !== null;
  const isAddressMapped = ADDRESS_FIELD_KEYS.every((key) => columnMapping[key] !== null);
  const mappingComplete = isReferenceMapped || isAddressMapped;
  const mappedColumnIndexes = mappingFields.map(({ key }) => columnMapping[key]).filter(
    (value) => value !== null
  );
  const mappingHasDuplicates =
    new Set(mappedColumnIndexes).size !== mappedColumnIndexes.length;

  const runPreview = async () => {
    setIsPreviewing(true);
    resetPreviewState();
    try {
      const result = await importBankAccountObjectAssignments(
        bankAccount.id,
        file,
        { dryRun: true, deleteRemoved: false, columnMapping }
      );
      setPreview(result);
      // Zeigt standardmäßig die erste Kategorie mit Einträgen an (Reihenfolge
      // siehe previewTabs), damit Auffälligkeiten (Fehler, Entfernen) nicht
      // hinter einer leeren "Neu"-Tab versteckt bleiben.
      const first_non_empty = previewTabs.find((tab) => result[tab.key]?.length > 0);
      setActiveTab(first_non_empty?.key || previewTabs[0].key);
    } catch (error) {
      enqueueSnackbar("Vorschau konnte nicht erstellt werden.", {
        variant: "error",
      });
      console.error("Failed to preview bank account object import", error);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleApply = async () => {
    if (!file) return;
    setIsApplying(true);
    try {
      await importBankAccountObjectAssignments(bankAccount.id, file, {
        dryRun: false,
        deleteRemoved: deleteConfirmed,
        columnMapping,
      });
      enqueueSnackbar(
        deleteConfirmed
          ? "Sync durchgeführt."
          : "Objekte wurden additiv hinzugefügt.",
        { variant: "success" }
      );
      setApplied(true);
      onSuccess?.();
    } catch (error) {
      enqueueSnackbar("Fehler beim Übernehmen des Imports.", {
        variant: "error",
      });
      console.error("Failed to apply bank account object import", error);
    } finally {
      setIsApplying(false);
    }
  };

  const handleBackToUpload = () => {
    // Zurück auf Schritt 1 - komplett neu starten, damit eine andere Datei
    // gewählt werden kann.
    setFile(null);
    resetMappingState();
  };

  const handleBackToMapping = () => {
    // Datei und Spalten-Mapping bleiben erhalten - nur die Vorschau wird
    // verworfen, damit das Mapping korrigiert werden kann.
    resetPreviewState();
  };

  const has_columns = !!columns && columns.length > 0;
  const has_preview = !!preview;
  const step = applied
    ? STEP.RESULT
    : has_preview || isPreviewing
      ? STEP.PREVIEW
      : has_columns
        ? STEP.MAPPING
        : STEP.UPLOAD;
  const has_changes =
    has_preview && (preview.to_add.length > 0 || preview.to_remove.length > 0);
  const can_apply =
    has_preview &&
    (preview.to_add.length > 0 || (deleteConfirmed && preview.to_remove.length > 0));
  const can_go_next = mappingComplete && !mappingHasDuplicates && !isPreviewing;

  const renderUploadStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Datei mit einer Header-Zeile. Objekte werden
        {objectRefNumLabel
          ? ` entweder über ${objectRefNumLabel} oder über`
          : " über"}{" "}
        PLZ/Ort/Straße/Hausnummer gefunden (Spaltenreihenfolge beliebig - die
        Zuordnung erfolgt im nächsten Schritt), eingeschränkt auf Objekte des
        Kunden dieses Bankkontos.
      </Typography>
      <Button variant="outlined" component="label" disabled={isLoadingColumns}>
        Datei auswählen
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".xlsx,.xls"
          onChange={handleFileChange}
        />
      </Button>
      {isLoadingColumns && <Spinner />}
    </>
  );

  const renderMappingStep = () => (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Datei: {file?.name}
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Spalten zuordnen
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Ordnen Sie jeder Eigenschaft links die passende Spalte aus Ihrer Datei
        rechts zu.{" "}
        {objectRefNumLabel
          ? `Es genügt entweder ${objectRefNumLabel} oder die komplette Adresse (PLZ/Ort/Straße/Hausnummer) - ist ${objectRefNumLabel} vorhanden, hat sie Vorrang, sonst wird über die Adresse gematcht.`
          : "Die komplette Adresse (PLZ/Ort/Straße/Hausnummer) muss zugeordnet werden."}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
        {mappingFields.map(({ key, label }) => (
          <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 110,
                flexShrink: 0,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor: "grey.100",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                {label}
              </Typography>
            </Box>
            <ArrowForwardIcon fontSize="small" color="action" />
            <TextField
              size="small"
              select
              // Ohne eigenes labelId setzt MUI aria-labelledby selbstreferenzierend
              // (Select zeigt auf sich selbst), wodurch ein zusätzliches aria-label
              // laut ARIA-Spec keine Wirkung auf den Accessible Name hätte - daher
              // zusätzlich ein stabiles data-testid für Tests und Screenreader-Zwecke.
              SelectProps={{ "aria-label": label, "data-testid": `mapping-select-${key}` }}
              value={columnMapping[key] ?? ""}
              onChange={(e) => handleMappingChange(key, e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">— wählen —</MenuItem>
              {columns.map((columnLabel, index) => (
                <MenuItem key={index} value={index}>
                  {columnLabel || `Spalte ${index + 1}`}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ))}
      </Box>
      {mappingHasDuplicates && (
        <Typography variant="caption" color="error" sx={{ display: "block" }}>
          Jede Spalte darf nur einem Feld zugeordnet werden.
        </Typography>
      )}
      {!mappingHasDuplicates && !mappingComplete && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          {objectRefNumLabel
            ? `Ordnen Sie entweder ${objectRefNumLabel} oder alle vier Adressfelder vollständig zu, um fortzufahren.`
            : "Ordnen Sie alle vier Adressfelder vollständig zu, um fortzufahren."}
        </Typography>
      )}
    </Box>
  );

  const renderPreviewStep = () => (
    <>
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={(_e, value) => setActiveTab(value)}>
            {previewTabs.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {tab.label}
                    <Chip size="small" color={tab.color} label={preview[tab.key].length} />
                  </Box>
                }
              />
            ))}
          </TabList>
        </Box>
        {previewTabs.map((tab) => (
          <TabPanel key={tab.key} value={tab.key} sx={{ px: 0 }}>
            {tab.key === "to_remove" && preview.to_remove.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Diese Objekte sind diesem Bankkonto aktuell zugeordnet, kommen
                in der importierten Datei aber nicht mehr vor und werden
                deshalb zum Entfernen vorgemerkt.
              </Typography>
            )}
            {tab.key === "unmatched" && preview.unmatched.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Die folgenden Einträge aus der Datei konnten keinem Objekt im
                Schadenportal zugeordnet werden.
              </Typography>
            )}
            <PreviewTable entries={preview[tab.key]} columns={tab.columns} />
          </TabPanel>
        ))}
      </TabContext>

      {/* Sync ist nur nach expliziter Bestätigung möglich - die Checkbox
          erläutert genau, was das Entfernen bedeutet. Muss vor "Jetzt
          importieren" entschieden werden, da der Import direkt ausgeführt
          wird. */}
      {preview.to_remove.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteConfirmed}
                onChange={(e) => setDeleteConfirmed(e.target.checked)}
                disabled={isApplying}
              />
            }
            label={`Sync durchführen: Die ${preview.to_remove.length} Objekte im Tab „Entfernen" werden dabei von diesem Bankkonto entfernt.`}
          />
        </Alert>
      )}
      {!has_changes && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Keine Änderungen gegenüber dem aktuellen Stand.
        </Typography>
      )}
    </>
  );

  // Schritt 4: reine Ergebnis-Bestätigung, keine weitere Aktion mehr möglich -
  // der Import wurde bereits in Schritt 3 ausgeführt.
  const renderResultStep = () => (
    <Alert severity="success">{buildResultSummary(preview, deleteConfirmed)}</Alert>
  );

  const renderActions = () => {
    switch (step) {
      case STEP.MAPPING:
        return (
          <>
            <Button variant="outlined" onClick={handleBackToUpload}>
              Zurück
            </Button>
            <Button variant="contained" onClick={runPreview} disabled={!can_go_next}>
              Weiter
            </Button>
          </>
        );
      case STEP.PREVIEW:
        return (
          <>
            <Button variant="outlined" onClick={handleBackToMapping} disabled={isApplying}>
              Zurück
            </Button>
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={!can_apply || isApplying || isPreviewing}
            >
              {isApplying ? "Wird importiert..." : "Jetzt importieren"}
            </Button>
          </>
        );
      case STEP.RESULT:
        return (
          <Button variant="contained" onClick={onClose}>
            Schließen
          </Button>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    setTca({
      title: "Objekte importieren",
      maxWidth: "md",
      content: (
        <Box>
          <Stepper activeStep={step} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Datei</StepLabel>
            </Step>
            <Step>
              <StepLabel>Spalten zuordnen</StepLabel>
            </Step>
            <Step>
              <StepLabel>Vorschau</StepLabel>
            </Step>
            <Step>
              <StepLabel>Importieren</StepLabel>
            </Step>
          </Stepper>

          {step === STEP.UPLOAD && renderUploadStep()}
          {step === STEP.MAPPING && renderMappingStep()}
          {isPreviewing && <Spinner />}
          {step === STEP.PREVIEW && !isPreviewing && renderPreviewStep()}
          {step === STEP.RESULT && renderResultStep()}
        </Box>
      ),
      actions: (
        <>
          {step !== STEP.RESULT && <Button onClick={onClose}>Abbrechen</Button>}
          {renderActions()}
        </>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    file,
    columns,
    columnMapping,
    isLoadingColumns,
    preview,
    activeTab,
    isPreviewing,
    isApplying,
    deleteConfirmed,
    applied,
    mappingFields,
    previewTabs,
    objectRefNumLabel,
  ]);

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      onClose={onClose}
    />
  );
}

BankAccountObjectImportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  bankAccount: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    kunde: PropTypes.shape({
      object_ref_num_label: PropTypes.string,
    }),
  }),
};
